import { useState, useEffect, useRef } from 'react';
import { FiGithub, FiLinkedin, FiTwitter, FiMail, FiExternalLink, FiSend, FiClock, FiMapPin, FiTerminal, FiCpu, FiArrowUp } from 'react-icons/fi';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import api from '../utils/api.js';

// Configure axios with retry logic for health check
axiosRetry(axios, {
  retries: 3, // Retry up to 3 times
  retryDelay: (retryCount) => retryCount * 1000, // 1s delay between retries
  retryCondition: (error) => true, // Retry on any error
});

const MagneticLink = ({ children, className }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouse = (e) => {
    const { clientX, clientY } = e;
    const { height, width, left, top } = ref.current.getBoundingClientRect();
    const middleX = clientX - (left + width / 2);
    const middleY = clientY - (top + height / 2);
    setPosition({ x: middleX * 0.3, y: middleY * 0.3 });
  };

  const reset = () => setPosition({ x: 0, y: 0 });

  const { x, y } = position;

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={reset}
      animate={{ x, y }}
      transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [localTime, setLocalTime] = useState('');
  const footerRef = useRef(null);
  const [availability, setAvailability] = useState({ status: 'Not Available', message: '' });
  const [isServerAwake, setIsServerAwake] = useState(false);
  const [loading, setLoading] = useState(true);

  // Define backend URL with a fallback
  const BACKEND_URL = 'https://minimum-karrah-granday-b79700a0.koyeb.app';

  useEffect(() => {
    // Log backend URL for debugging
    console.log('Backend URL:', BACKEND_URL);

    const checkServerHealth = async () => {
      try {
        if (!BACKEND_URL || BACKEND_URL.includes('undefined')) {
          throw new Error('Backend URL is not defined. Please set REACT_APP_BACKEND_URL in Vercel.');
        }
        const response = await axios.get(`${BACKEND_URL}/api/health`);
        if (response.status === 200 && response.data.status === 'OK') {
          setIsServerAwake(true);
        }
      } catch (error) {
        console.error('Server wake-up error:', error);
        setAvailability({ status: 'Not Available', message: 'Failed to connect to server' });
        setLoading(false);
      }
    };

    const fetchAvailability = async () => {
      try {
        const res = await api.get('/api/availability');
        setAvailability(res.data);
      } catch (error) {
        console.error('Error fetching availability:', error);
        setAvailability({ status: 'Not Available', message: 'Error fetching availability' });
      } finally {
        setLoading(false);
      }
    };

    // First check server health, then fetch availability if awake
    checkServerHealth().then(() => {
      if (isServerAwake) {
        fetchAvailability();
      }
    });
  }, [isServerAwake, BACKEND_URL]);

  useEffect(() => {
    const timer = setInterval(() => {
      const time = new Date().toLocaleTimeString('en-US', {
        timeZone: 'Asia/Kabul',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      setLocalTime(time);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const getStatusColors = () => {
    if (loading || !isServerAwake) return { dot: 'bg-gray-500', ping: 'bg-gray-400' };
    switch (availability.status) {
      case 'Open to Work':
        return { dot: 'bg-green-500', ping: 'bg-green-400' };
      case 'Available for Freelance':
        return { dot: 'bg-blue-500', ping: 'bg-blue-400' };
      default:
        return { dot: 'bg-red-500', ping: 'bg-red-400' };
    }
  };

  const statusColors = getStatusColors();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer ref={footerRef} className="relative bg-[#050505] pt-32 pb-12 overflow-hidden noise-bg">
      {/* Dynamic Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[300px] bg-secondary_accent/5 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-24">
          
          {/* Bento Block 1: Branding & Status */}
          <div className="lg:col-span-5 space-y-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-[2.5rem] bg-white/[0.03] border border-white/5 backdrop-blur-3xl relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:rotate-12 transition-transform duration-700">
                <FiCpu size={80} />
              </div>
              
              <Link to="/home" className="inline-flex items-center gap-3 mb-8 group/logo">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary_accent to-accent flex items-center justify-center text-dark-900 font-black text-3xl group-hover/logo:scale-110 transition-all duration-500 shadow-[0_0_30px_rgba(251,191,36,0.3)]">
                  G
                </div>
                <div className="flex flex-col">
                  <span className="text-3xl font-bold tracking-tighter text-white font-clash leading-none">Granday</span>
                  <span className="text-[10px] font-black uppercase tracking-[0.4em] text-secondary_accent/60">Software Engineer</span>
                </div>
              </Link>

              <p className="text-gray-400 font-light leading-relaxed text-lg max-w-md mb-10">
                Pushing the boundaries of <span className="text-white font-medium">Digital Craftsmanship</span> through code, design, and decentralization.
              </p>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/5 border border-white/10">
                  <div className="relative flex h-2 w-2">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${statusColors.ping} opacity-75`}></span>
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${statusColors.dot}`}></span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-300">
                    Live Status: {loading || !isServerAwake ? 'Loading...' : availability.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/5 border border-white/10">
                  <FiClock className="text-secondary_accent" size={14} />
                  <span className="text-xs font-mono text-gray-300 tracking-tighter">{localTime}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bento Block 2: Quick Terminal / Socials */}
          <div className="lg:col-span-3">
            <motion.div 
              className="h-full p-8 rounded-[2.5rem] bg-white/[0.03] border border-white/5 backdrop-blur-3xl flex flex-col justify-between"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="space-y-8">
                <div className="flex items-center gap-2 text-gray-500">
                  <FiTerminal size={14} />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]">Quick Links</span>
                </div>
                <ul className="space-y-4">
                  {['Projects', 'Blog', 'About', 'Contact'].map((item) => (
                    <li key={item}>
                      <Link 
                        to={`/${item.toLowerCase()}`} 
                        className="text-base text-gray-400 hover:text-white flex items-center justify-between group transition-all"
                      >
                        {item}
                        <FiExternalLink className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all text-secondary_accent" size={14} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-3 mt-12">
                {[
                  { icon: <FiGithub />, href: "https://github.com/MrGranday" },
                  { icon: <FiLinkedin />, href: "https://www.linkedin.com/in/osman-ghani-granday-368b18259/" },
                  { icon: <FiTwitter />, href: "https://x.com/OGranday" }
                ].map((social, i) => (
                  <MagneticLink key={i}>
                    <a 
                      href={social.href} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 text-gray-400 hover:text-secondary_accent hover:border-secondary_accent/30 transition-all"
                    >
                      {social.icon}
                    </a>
                  </MagneticLink>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Bento Block 3: The Message / Action */}
          <div className="lg:col-span-4">
            <motion.div 
              className="h-full p-8 rounded-[2.5rem] bg-gradient-to-br from-secondary_accent/10 to-transparent border border-secondary_accent/20 backdrop-blur-3xl relative overflow-hidden group flex flex-col justify-between"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="relative z-10">
                <h4 className="text-3xl font-bold text-white mb-4 font-clash leading-tight">Have a <span className="text-secondary_accent">vision</span> in mind?</h4>
                <p className="text-gray-400 font-light mb-8">Let's build something exceptional together. I'm currently taking new projects.</p>
                
                <MagneticLink>
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-secondary_accent text-dark-900 font-bold uppercase tracking-widest text-xs hover:bg-white transition-all shadow-xl shadow-secondary_accent/20"
                  >
                    Start a conversation <FiSend size={16} />
                  </Link>
                </MagneticLink>
              </div>

              {/* Decorative Console Lines */}
              <div className="mt-12 font-mono text-[10px] text-secondary_accent/30 space-y-1 select-none pointer-events-none">
                <div className="flex gap-2"><span>></span> <span>system.initialize()</span></div>
                <div className="flex gap-2"><span>></span> <span>fetching_latest_build...</span></div>
                <div className="flex gap-2"><span>></span> <span className="text-green-500/40">granday_portfolio_v1.0.2_stable</span></div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar 2.0 */}
        <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-12">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-600">
              © {currentYear} Granday Studio
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-[10px] font-bold uppercase tracking-widest text-gray-600 hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="text-[10px] font-bold uppercase tracking-widest text-gray-600 hover:text-white transition-colors">Legal Terms</a>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <motion.button 
              onClick={scrollToTop}
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all"
            >
              <FiArrowUp size={20} />
            </motion.button>
            <div className="h-8 w-px bg-white/10" />
            <div className="flex flex-col items-end">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">Developed & Design By </span>
              {/* <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-secondary_accent">Glass + Noise v2.0</span> */}
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-secondary_accent">Osman Ghani Granday</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
